import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links, subscriptionDetailsRouteUrl } from 'utils/featureToggles'

import { SubscriptionNavigation } from './SubscriptionNavigation'
import { SubscriptionsDetailsMainPage } from './SubscriptionsDetailsPage'
import { SubscriptionsMainPage } from './SubscriptionsMainPage'
import { SubscriptionsPlatformPage } from './SubscriptionsPlatformPage'

export enum SubscriptionTab {
  MAIN = 'main',
  DETAILS = 'details',
  PLATFORMS = 'platforms',
}

export const ResolveSubscriptionRoute = (): JSX.Element => {
  const { id, slug } = useParams<{ id: string; slug: SubscriptionTab }>()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [crumbs, setCrumbs] = useState<{ title: string; link: string }[]>([
    { title: 'Subscriptions', link: links.administrationSubscriptions.url },
  ])

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () =>
      id ? tradingAccountsApiClient.getTradingAccountSubscriptionById(id) : Promise.resolve(null),
    []
  )
  const { data, isLoading } = useFetchOne(fetchData)

  const matchesSlug = (subscriptionTab?: string) => {
    if (!subscriptionTab) {
      return false
    }
    return (
      subscriptionTab === SubscriptionTab.MAIN ||
      subscriptionTab === SubscriptionTab.DETAILS ||
      subscriptionTab === SubscriptionTab.PLATFORMS
    )
  }

  useEffect(() => {
    if (data) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: data?.name || '',
          link: subscriptionDetailsRouteUrl(
            data,
            slug ? (matchesSlug(slug) ? slug : SubscriptionTab.MAIN) : SubscriptionTab.MAIN
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!data) {
      return <PageNotFound />
    }
    if (slug === SubscriptionTab.MAIN) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <SubscriptionNavigation />
          <SubscriptionsMainPage subscription={data} onRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (slug === SubscriptionTab.DETAILS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <SubscriptionNavigation />
          <SubscriptionsDetailsMainPage subscription={data} onRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (slug === SubscriptionTab.PLATFORMS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <SubscriptionNavigation />
          <SubscriptionsPlatformPage subscription={data} onRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
}
